<template>
  <!--
    WHAT — Structured Analysis (patient_dossier) detail tab "Test". Runs this SAT
      against one MBI synchronously via the test-patient action and shows the
      result in two panels: RAW JSON and FORMATTED (via DynamicRenderer, driven
      by the SAT's saved display_schema). Nothing is persisted.
    NOTES — selfChrome: true; renders its own titled cards. Read-only (no patch).
  -->
  <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100">
      <span class="text-[13px] font-semibold text-gray-800">Test on one patient</span>
      <p class="text-[11px] text-gray-400">Run this dossier against a single MBI and inspect the output. Nothing is saved.</p>
    </div>
    <div class="p-5 space-y-4">
      <div class="space-y-3">
        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Patient MBI</label>
          <input
            v-model="testMbi"
            type="text"
            spellcheck="false"
            placeholder="e.g. 1A2B3C4D5E6"
            class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg font-mono bg-white focus:outline-none focus:border-blue-400"
            @keydown.enter="maybeRun"
          />
        </div>

        <!-- Filter inputs — one per run_context key referenced by a data
             packet's filters ({key} templates). All are REQUIRED before Run,
             because the packet declared it needs them. -->
        <div v-if="filterKeys.length" class="space-y-2 border border-gray-100 rounded-lg p-3 bg-gray-50/50">
          <div class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Variables</div>
          <div v-for="key in filterKeys" :key="key">
            <label class="block text-[11px] font-medium text-gray-600 mb-1">
              {{ key }} <span class="text-red-500">*</span>
            </label>
            <input
              v-model="filterValues[key]"
              type="text"
              spellcheck="false"
              :placeholder="`value for {${key}}`"
              class="w-full text-xs px-2 py-1.5 border rounded-lg font-mono bg-white focus:outline-none"
              :class="filterValues[key] && String(filterValues[key]).trim() ? 'border-gray-200 focus:border-blue-400' : 'border-amber-300 focus:border-amber-400'"
              @keydown.enter="maybeRun"
            />
          </div>
          <p class="text-[10px] text-gray-400 leading-snug">
            This SAT's prompt blocks are driven by these variables. Fill every one to run (at fire time they come from the opportunity).
          </p>
        </div>

        <div class="flex items-center gap-2">
          <button
            class="text-xs px-3 py-2 rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed"
            :style="{ background: artifact.accent }"
            :disabled="!canRun || testing"
            @click="runTest"
          >{{ testing ? 'Running…' : 'Run' }}</button>
          <span v-if="testMbi.trim() && missingFilters.length" class="text-[11px] text-amber-600">
            Missing: {{ missingFilters.join(', ') }}
          </span>
        </div>
      </div>
      <p v-if="testError" class="text-[11px] text-red-500">{{ testError }}</p>

      <!-- Formatted on top, raw below -->
      <div v-if="testResult" class="space-y-4">
        <!-- Formatted -->
        <div class="min-w-0">
          <div class="text-[11px] font-medium text-gray-600 mb-1">Formatted</div>
          <div class="border border-gray-200 rounded-lg bg-gray-50 overflow-auto max-h-[36rem]">
            <DynamicRenderer
              v-if="displaySchema"
              :data="testResult"
              :display-schema="displaySchema"
            />
            <div v-else class="text-[11px] text-gray-400 p-4">
              No display schema on this analysis — set one in the Output tab to see a formatted render.
            </div>
          </div>
        </div>
        <!-- Raw -->
        <div class="min-w-0">
          <div class="text-[11px] font-medium text-gray-600 mb-1">Raw output</div>
          <pre class="text-[11px] px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 overflow-auto max-h-[36rem] leading-relaxed">{{ testResultText }}</pre>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import DynamicRenderer from '@/components/widgetcomponents/JSON_Editors/DynamicRenderer.vue';

export default {
  name: 'StructuredTestSection',
  components: { DynamicRenderer },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  data() {
    return { testMbi: '', testing: false, testError: null, testResult: null, filterValues: {} };
  },
  computed: {
    testResultText() {
      try { return JSON.stringify(this.testResult, null, 2); } catch { return String(this.testResult); }
    },
    // Saved display schema from config; null if absent/empty so the formatted
    // panel shows the "set one" hint instead of an empty render.
    displaySchema() {
      const ds = (this.record.config || {}).display_schema;
      return (ds && Array.isArray(ds.elements) && ds.elements.length) ? ds : null;
    },
    // run_context keys this SAT needs. Authoritative source: the VARIABLE
    // declared on each prompt block (config.prompt_blocks[].variable). Also
    // honors {key} templates in data-packet filters (the DA-contract path) and
    // config.run_context_keys, so nothing is missed. Any key here is a required
    // test input — Run is blocked until it's provided.
    filterKeys() {
      const cfg = this.record.config || {};
      const keys = new Set(cfg.run_context_keys || []);
      for (const b of (cfg.prompt_blocks || [])) {
        if (b.variable && b.variable.trim()) keys.add(b.variable.trim());
      }
      for (const p of (cfg.data_packets || [])) {
        for (const raw of Object.values(p.filters || {})) {
          if (typeof raw === 'string' && raw.startsWith('{') && raw.endsWith('}')) {
            keys.add(raw.slice(1, -1));
          }
        }
      }
      return [...keys];
    },
    missingFilters() {
      return this.filterKeys.filter((k) => !this.filterValues[k] || !String(this.filterValues[k]).trim());
    },
    canRun() {
      return !!this.testMbi.trim() && this.missingFilters.length === 0;
    },
  },
  methods: {
    maybeRun() {
      if (this.canRun && !this.testing) this.runTest();
    },
    async runTest() {
      const mbi = this.testMbi.trim();
      if (!mbi || this.missingFilters.length) return;  // guard: all declared filters present
      this.testing = true;
      this.testError = null;
      this.testResult = null;
      // Build run_context from the filled filter inputs (numeric-coerce when possible).
      const run_context = {};
      for (const k of this.filterKeys) {
        const v = String(this.filterValues[k]).trim();
        run_context[k] = (v !== '' && !isNaN(Number(v))) ? Number(v) : v;
      }
      try {
        const url = this.artifact.detailUrl(this.record[this.artifact.idField]) + 'test-patient/';
        const { data } = await axios.post(url, { mbi, run_context });
        this.testResult = data.result;
      } catch (e) {
        this.testError = e.response?.data?.error || e.message || 'Test run failed';
      } finally {
        this.testing = false;
      }
    },
  },
};
</script>
